import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Stack,
    Chip,
    Avatar,
    LinearProgress,
    Divider,
    IconButton
} from '@mui/material';
import {
    TrendingUp,
    AccountBalance,
    ShowChart,
    AttachMoney,
    People,
    MoreVert,
    ArrowUpward,
    ArrowDownward
} from '@mui/icons-material';

const Dashboard: React.FC = () => {
    // Mock data
    const portfolioValue = 1247500;
    const dailyChange = 2.34;
    const monthlyChange = 8.67;
    const totalHoldings = 47;
    const cashBalance = 125000;

    const recentTransactions = [
        { id: 1, type: 'Buy', symbol: 'AAPL', shares: 50, price: 175.50, time: '2 hours ago' },
        { id: 2, type: 'Sell', symbol: 'MSFT', shares: 25, price: 325.75, time: '1 day ago' },
        { id: 3, type: 'Buy', symbol: 'GOOGL', shares: 10, price: 142.30, time: '2 days ago' },
        { id: 4, type: 'Buy', symbol: 'TSLA', shares: 15, price: 245.80, time: '3 days ago' }
    ];

    const topHoldings = [
        { symbol: 'AAPL', name: 'Apple Inc.', value: 245000, change: 2.1, weight: 19.6 },
        { symbol: 'MSFT', name: 'Microsoft Corp.', value: 198000, change: -0.8, weight: 15.9 },
        { symbol: 'GOOGL', name: 'Alphabet Inc.', value: 156000, change: 1.5, weight: 12.5 },
        { symbol: 'TSLA', name: 'Tesla Inc.', value: 134000, change: 3.2, weight: 10.7 },
        { symbol: 'AMZN', name: 'Amazon.com Inc.', value: 112000, change: -1.2, weight: 9.0 }
    ];

    const performanceMetrics = [
        { label: 'YTD Return', value: '12.4%', change: '+2.1%', positive: true },
        { label: '1Y Return', value: '18.7%', change: '+3.2%', positive: true },
        { label: '3Y Return', value: '24.3%', change: '+1.8%', positive: true },
        { label: 'Volatility', value: '14.2%', change: '-0.5%', positive: true }
    ];

    return (
        <Box sx={{ p: 3, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700, color: '#18010a' }}>
                    Dashboard
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Welcome back! Here's an overview of your portfolio performance.
                </Typography>
            </Box>

            {/* Portfolio Summary Cards */}
            <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 3, 
                mb: 4,
                '& > *': { flex: '1 1 280px', minWidth: '280px' }
            }}>
                <Card sx={{ 
                    bgcolor: 'white', 
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    borderRadius: 3,
                    '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.3s ease' }
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2' }}>
                                <AccountBalance />
                            </Avatar>
                            <IconButton size="small">
                                <MoreVert />
                            </IconButton>
                        </Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#18010a', mb: 1 }}>
                            ${(portfolioValue / 1000000).toFixed(2)}M
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Portfolio Value
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TrendingUp sx={{ color: '#4caf50', mr: 1, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                +${dailyChange.toFixed(2)}k today
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ 
                    bgcolor: 'white', 
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    borderRadius: 3,
                    '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.3s ease' }
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Avatar sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2' }}>
                                <ShowChart />
                            </Avatar>
                            <IconButton size="small">
                                <MoreVert />
                            </IconButton>
                        </Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#18010a', mb: 1 }}>
                            {totalHoldings}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Total Holdings
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TrendingUp sx={{ color: '#4caf50', mr: 1, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                +2 this month
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ 
                    bgcolor: 'white', 
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    borderRadius: 3,
                    '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.3s ease' }
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Avatar sx={{ bgcolor: '#e8f5e8', color: '#388e3c' }}>
                                <AttachMoney />
                            </Avatar>
                            <IconButton size="small">
                                <MoreVert />
                            </IconButton>
                        </Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#18010a', mb: 1 }}>
                            ${(cashBalance / 1000).toFixed(0)}k
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Available Cash
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                                Ready to invest
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ 
                    bgcolor: 'white', 
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    borderRadius: 3,
                    '&:hover': { transform: 'translateY(-2px)', transition: 'all 0.3s ease' }
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Avatar sx={{ bgcolor: '#fff3e0', color: '#f57c00' }}>
                                <People />
                            </Avatar>
                            <IconButton size="small">
                                <MoreVert />
                            </IconButton>
                        </Box>
                        <Typography variant="h4" sx={{ fontWeight: 700, color: '#18010a', mb: 1 }}>
                            {monthlyChange.toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Monthly Return
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TrendingUp sx={{ color: '#4caf50', mr: 1, fontSize: 20 }} />
                            <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                Above benchmark
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            {/* Performance Metrics and Recent Transactions */}
            <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                gap: 3, 
                mb: 4,
                '& > *': { flex: '1 1 400px' }
            }}>
                <Card sx={{ 
                    bgcolor: 'white', 
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    borderRadius: 3,
                    height: 'fit-content'
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, color: '#18010a' }}>
                                Performance Metrics
                            </Typography>
                            <Chip label="Last 30 days" size="small" sx={{ bgcolor: '#f0f0f0' }} />
                        </Box>
                        <Box sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                            gap: 2
                        }}>
                            {performanceMetrics.map((metric, index) => (
                                <Box key={index} sx={{ textAlign: 'center', p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {metric.label}
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#18010a', mb: 1 }}>
                                        {metric.value}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        {metric.positive ? (
                                            <ArrowUpward sx={{ color: '#4caf50', fontSize: 16, mr: 0.5 }} />
                                        ) : (
                                            <ArrowDownward sx={{ color: '#f44336', fontSize: 16, mr: 0.5 }} />
                                        )}
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: metric.positive ? '#4caf50' : '#f44336',
                                                fontWeight: 600 
                                            }}
                                        >
                                            {metric.change}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ 
                    bgcolor: 'white', 
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    borderRadius: 3,
                    height: 'fit-content'
                }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#18010a', mb: 3 }}>
                            Recent Transactions
                        </Typography>
                        <Stack spacing={2}>
                            {recentTransactions.map((transaction) => (
                                <Box key={transaction.id} sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#18010a' }}>
                                            {transaction.symbol}
                                        </Typography>
                                        <Chip 
                                            label={transaction.type} 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: transaction.type === 'Buy' ? '#e8f5e8' : '#ffebee',
                                                color: transaction.type === 'Buy' ? '#2e7d32' : '#c62828',
                                                fontSize: '0.7rem'
                                            }} 
                                        />
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {transaction.shares} shares @ ${transaction.price}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        {transaction.time}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </CardContent>
                </Card>
            </Box>

            {/* Top Holdings */}
            <Card sx={{ 
                bgcolor: 'white', 
                boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                borderRadius: 3
            }}>
                <CardContent sx={{ p: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#18010a', mb: 3 }}>
                        Top Holdings
                    </Typography>
                    <Stack spacing={2}>
                        {topHoldings.map((holding, index) => (
                            <Box key={holding.symbol}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#18010a', minWidth: 60 }}>
                                            {holding.symbol}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                            {holding.name}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#18010a' }}>
                                            ${(holding.value / 1000).toFixed(0)}k
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {holding.change > 0 ? (
                                                <ArrowUpward sx={{ color: '#4caf50', fontSize: 16, mr: 0.5 }} />
                                            ) : (
                                                <ArrowDownward sx={{ color: '#f44336', fontSize: 16, mr: 0.5 }} />
                                            )}
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    color: holding.change > 0 ? '#4caf50' : '#f44336',
                                                    fontWeight: 600,
                                                    minWidth: 40
                                                }}
                                            >
                                                {holding.change > 0 ? '+' : ''}{holding.change}%
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <LinearProgress 
                                        variant="determinate" 
                                        value={holding.weight} 
                                        sx={{ 
                                            flexGrow: 1, 
                                            height: 6, 
                                            borderRadius: 3,
                                            bgcolor: '#f0f0f0',
                                            '& .MuiLinearProgress-bar': {
                                                bgcolor: '#C10B50'
                                            }
                                        }} 
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 40 }}>
                                        {holding.weight}%
                                    </Typography>
                                </Box>
                                {index < topHoldings.length - 1 && <Divider sx={{ mt: 2 }} />}
                            </Box>
                        ))}
                    </Stack>
                </CardContent>
            </Card>
        </Box>
    );
};

export default Dashboard;
